import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import ModalBox from 'yoda-core-components/lib/components/ModalBox/ModalBox';
import Close from 'yoda-core-components/lib/assets/svg/Close.svg';
import Challenge from '../../assets/images/challenge.svg';
import { getChallenges, acceptChallenge } from '../../actions/ChallengesAction';

const formatChallengeDate = (date = '') => {
    const datePart = date.toString().split('T')[0];
    const [year, month, day] = datePart.split('-');

    return year && month && day ? `${month}/${day}/${year}` : date;
};

const FALLBACK_EXCLUSIONS_PATH = '/m/rewards-redemptions-exclusions?pageId=pg40067900006';

const Challenges = () => {
    const dispatch = useDispatch();
    const { challenges = [], showLoader } = useSelector((state) => state.challengesDetails) || {};
    const exclusionsUrl = useSelector((state) => state.context?.preferences?.exclusionsUrl);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    useEffect(() => {
        dispatch(getChallenges());
    }, [dispatch]);

    const activeChallenges = challenges?.filter(
        (c) => c.status === 'NotStarted' || c.status === 'InProgress'
    );
    const historyChallenges = challenges?.filter(
        (c) => c.status === 'Completed' || c.status === 'Expired'
    );
    const allChallengesCompleted =
        challenges?.length > 0 && challenges.every((c) => c.status === 'Completed');

    const handleAcceptChallenge = (offerId) => {
        dispatch(acceptChallenge(offerId));
    };

    const renderProgressBar = (progressCompleted, threshold) => {
        const percentage = Math.min((progressCompleted / threshold) * 100, 100);
        return (
            <div className={dt(['flex', 'items-center', 'gap-2', 'mt-2'])}>
                <div
                    className={dt([
                        'flex-1',
                        'h-[19px]',
                        'bg-gray-15',
                        'rounded-full',
                        'relative',
                        'overflow-hidden',
                    ])}
                >
                    <div
                        className={dt([
                            'h-[19px]',
                            'bg-brand-red',
                            'rounded-full',
                            'flex',
                            'items-center',
                            'justify-center',
                        ])}
                        style={{ width: `${percentage}%` }}
                    >
                        {percentage !== 0 && (
                            <span className={dt(['text-white', 'text-small', 'font-semibold'])}>
                                {progressCompleted} of {threshold}
                            </span>
                        )}
                    </div>
                </div>
                <span className={dt(['text-normal', 'font-semibold'])}>{threshold}</span>
            </div>
        );
    };

    const renderChallengeCard = (challenge) => {
        const isNotStarted = challenge.status === 'NotStarted';
        const isInProgress = challenge.status === 'InProgress';

        return (
            <div
                key={challenge.offerId}
                className={dt(['border-b', 'border-solid', 'border-gray-15', 'py-4', 'bg-white'])}
            >
                <div
                    className={dt([
                        'flex',
                        'justify-between',
                        'items-center',
                        'smOnly:flex-col',
                        'smOnly:items-start',
                        'px-4',
                    ])}
                >
                    <div className={dt(['flex-1'])}>
                        <div className={dt(['font-bold', 'text-normal', 'text-black'])}>
                            {challenge.description}
                        </div>
                        <div className={dt(['flex', 'items-center', 'gap-2', 'mt-1'])}>
                            <span className={dt(['text-small', 'text-gray-60'])}>
                                End on {formatChallengeDate(challenge.endDate)}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    const url =
                                        exclusionsUrl ||
                                        `${window.location.origin}${FALLBACK_EXCLUSIONS_PATH}`;
                                    window.open(url, '_blank');
                                }}
                                className={dt(['text-small', 'text-black', 'underline'])}
                            >
                                Exclusions & Details
                            </button>
                        </div>
                    </div>
                    <div className={dt(['ml-4', 'smOnly:ml-0', 'smOnly:mt-4'])}>
                        {isNotStarted && (
                            <button
                                type="button"
                                onClick={() => handleAcceptChallenge(challenge.offerId)}
                                className={dt([
                                    'text-small',
                                    'font-semibold',
                                    'px-4',
                                    'py-2',
                                    'border',
                                    'border-solid',
                                    'border-gray-40',
                                    'rounded-lg',
                                    'bg-white',
                                    'text-black',
                                    'whitespace-nowrap',
                                ])}
                            >
                                Start Challenge
                            </button>
                        )}
                    </div>
                </div>

                {isInProgress && (
                    <div className={dt(['mt-2', 'px-3'])}>
                        {renderProgressBar(challenge.progressCompleted, challenge.threshold)}
                    </div>
                )}
            </div>
        );
    };

    const renderHistoryItem = (challenge) => {
        const isCompleted = challenge.status === 'Completed';
        const statusDate = formatChallengeDate(challenge.optInDate || challenge.endDate);
        const statusText = isCompleted
            ? `Completed on ${statusDate}`
            : `Expired on ${formatChallengeDate(challenge.endDate)}`;

        return (
            <div
                key={challenge.offerId}
                className={dt(['border-b', 'border-solid', 'border-gray-15', 'p-4'])}
            >
                <div className={dt(['font-bold', 'text-normal', 'text-black'])}>
                    {challenge.description}
                </div>
                <div
                    className={dt([
                        'text-small',
                        'mt-1',
                        isCompleted ? 'text-green-600' : 'text-gray-60',
                    ])}
                >
                    {statusText}
                </div>
            </div>
        );
    };

    const renderEmptyState = () => (
        <div className={dt(['flex', 'flex-col', 'items-center', 'py-10'])}>
            <Icon iconType="svg" width="48px" height="48px" viewBox="0 0 16 16">
                <Challenge />
            </Icon>
            <div className={dt(['font-semibold', 'text-normal', 'mt-4', 'text-center'])}>
                No challenges available
            </div>
            <div className={dt(['text-small', 'text-gray-60', 'mt-2', 'text-center'])}>
                Check back soon for new challenges to earn bonus points!
            </div>
        </div>
    );

    const renderCompletedState = () => (
        <div className={dt(['flex', 'flex-col', 'items-center', 'py-4', 'px-4', 'bg-light-white'])}>
            <div className={dt(['text-large', 'text-black'])}>
                You’ve completed all your challenges. Great job! Check back soon for new
                opportunities.
            </div>
        </div>
    );

    return (
        <div className={dt(['w-full', 'float-left', 'mt-4', 'mb-4'])}>
            {showLoader && <Loader keepOverlay automationId="challenges-loader" />}
            <div className={dt(['flex', 'items-center', 'gap-2'])}>
                <h2 className={dt(['text-xlarge', 'font-bold', 'mb-2'])}>Challenges</h2>
            </div>

            {!showLoader && allChallengesCompleted && renderCompletedState()}

            {!showLoader &&
                !allChallengesCompleted &&
                activeChallenges?.length === 0 &&
                renderEmptyState()}

            {activeChallenges?.length > 0 && (
                <div>{activeChallenges?.map((challenge) => renderChallengeCard(challenge))}</div>
            )}

            {historyChallenges?.length > 0 && (
                <button
                    type="button"
                    onClick={() => setShowHistoryModal(true)}
                    className={dt([
                        'text-small',
                        'text-black',
                        'underline',
                        'mt-4',
                        'smOnly-block',
                        'smOnly-mx-auto',
                    ])}
                >
                    View Challenge History
                </button>
            )}

            <ModalBox
                defaultCloseBtn={false}
                showModal={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
            >
                <div>
                    <div className={dt(['flex', 'justify-between', 'items-center', 'mb-5'])}>
                        <h3 className={dt(['text-xlarge', 'font-semibold'])}>Challenge History</h3>
                        <button type="button" onClick={() => setShowHistoryModal(false)}>
                            <Icon iconType="svg" width="32px" height="32px" viewBox="0 0 32 32">
                                <Close />
                            </Icon>
                        </button>
                    </div>
                    <div>{historyChallenges.map((challenge) => renderHistoryItem(challenge))}</div>
                </div>
            </ModalBox>
        </div>
    );
};

export default Challenges;
